// Display helpers for beneficiary accounts: labels, destinations, detail rows
// (§4.1), edit card lines (§5.2) and review rows (§5.4). Includes the §17 web
// corrections: country NAME not code (§17.3), bill-payment rows show
// Category/Biller/Consumer number (§17.4).
import type {
  BeneficiaryAccount,
  CountryRef,
  MobileSubType,
  NewAccountInput,
  PostalAddress,
} from '@/types/phase2.types';
import { DEFAULT_ROUTING, ROUTING_CODES } from '@/mock/data';
import { maskAccount } from '@/mock/util';
import { isNovaIfsc } from '@/utils/payments';

export const SUB_TYPE_LABELS: Record<MobileSubType, string> = {
  UPI_ID: 'UPI ID',
  MOBILE_NUMBER: 'Mobile number',
  MERCHANT_UPI: 'Merchant UPI',
  MOBILE_RECHARGE: 'Mobile recharge',
  BILL_PAYMENT: 'Bill payment',
};

export const KIND_TITLES: Record<BeneficiaryAccount['kind'], string> = {
  MOBILE_TRANSFER: 'Send To Mobile',
  LOCAL_TRANSFER: 'Local Transfer',
  INTERNATIONAL: 'International Transfer',
};

export const KIND_ORDER: BeneficiaryAccount['kind'][] = [
  'MOBILE_TRANSFER',
  'LOCAL_TRANSFER',
  'INTERNATIONAL',
];

export interface LabelValue {
  label: string;
  value: string;
  /** Render with an eye toggle (masked by default). */
  secret?: boolean;
  /** The full unmasked value shown when the eye toggle is on. */
  fullValue?: string;
}

export const formatMobile = (mobile: string): string => `+91 ${mobile}`;

/** The holder/account name shown for an account (payee name in payments). */
export const accountHolderName = (account: BeneficiaryAccount): string =>
  account.kind === 'MOBILE_TRANSFER' ? account.holderName : account.accountName;

const formatAddress = (addr?: PostalAddress): string =>
  addr ? [addr.line1, addr.line2, addr.postCode, addr.town].filter(Boolean).join(', ') : '';

export const countryNameOf = (code: string, countries: CountryRef[]): string =>
  countries.find((c) => c.code === code)?.name ?? code;

/** Short destination used in lists, payee pickers and receipts. */
export const accountDestination = (account: BeneficiaryAccount, countries: CountryRef[] = []): string => {
  switch (account.kind) {
    case 'MOBILE_TRANSFER':
      if (account.subType === 'MOBILE_NUMBER' || account.subType === 'MOBILE_RECHARGE') {
        return formatMobile(account.mobile ?? '');
      }
      if (account.subType === 'BILL_PAYMENT') return `${account.biller ?? ''} · ${account.consumerNumber ?? ''}`;
      return account.upiId ?? '';
    case 'LOCAL_TRANSFER':
      return `${account.bankName} · ${maskAccount(account.accountNumber)}`;
    case 'INTERNATIONAL':
      return `${countryNameOf(account.countryCode, countries)} · ${maskAccount(account.iban ?? account.accountNumber ?? '')}`;
  }
};

/** Rows for the detail panel (§4.1). */
export const detailRows = (account: BeneficiaryAccount, countries: CountryRef[]): LabelValue[] => {
  switch (account.kind) {
    case 'MOBILE_TRANSFER': {
      const rows: LabelValue[] = [{ label: 'Type', value: SUB_TYPE_LABELS[account.subType] }];
      if (account.subType === 'BILL_PAYMENT') {
        // §17.4 — never "UPI ID: —" for a bill-payment account.
        rows.push(
          { label: 'Category', value: account.billCategory ?? '' },
          { label: 'Biller', value: account.biller ?? '' },
          { label: 'Consumer number', value: account.consumerNumber ?? '' }
        );
      } else if (account.subType === 'MOBILE_NUMBER' || account.subType === 'MOBILE_RECHARGE') {
        rows.push({ label: 'Mobile', value: formatMobile(account.mobile ?? '') });
      } else {
        rows.push({ label: 'UPI ID', value: account.upiId ?? '' });
      }
      return rows;
    }
    case 'LOCAL_TRANSFER':
      return [
        { label: 'Bank', value: account.bankName },
        { label: 'IFSC', value: account.ifsc },
        {
          label: 'Account no.',
          value: maskAccount(account.accountNumber),
          secret: true,
          fullValue: account.accountNumber,
        },
      ];
    case 'INTERNATIONAL': {
      const rows: LabelValue[] = [
        // §17.3 — show the country name, not the code.
        { label: 'Country', value: countryNameOf(account.countryCode, countries) },
        { label: 'Bank', value: account.bankName },
      ];
      if (account.iban) {
        rows.push({ label: 'IBAN', value: maskAccount(account.iban), secret: true, fullValue: account.iban });
      } else {
        rows.push({
          label: 'Account no.',
          value: maskAccount(account.accountNumber ?? ''),
          secret: true,
          fullValue: account.accountNumber ?? '',
        });
      }
      if (account.swift) rows.push({ label: 'SWIFT', value: account.swift });
      return rows;
    }
  }
};

/** One-line summary for saved accounts in edit mode (§5.2). */
export const editCardLine = (account: BeneficiaryAccount, countries: CountryRef[]): string => {
  switch (account.kind) {
    case 'MOBILE_TRANSFER':
      if (account.subType === 'BILL_PAYMENT') {
        return `${SUB_TYPE_LABELS[account.subType]} · ${account.biller ?? ''} · ${account.consumerNumber ?? ''}`;
      }
      return `${SUB_TYPE_LABELS[account.subType]} · ${account.upiId ?? formatMobile(account.mobile ?? '')}`;
    case 'LOCAL_TRANSFER':
      return `${account.bankName} · ${account.ifsc} · ${maskAccount(account.accountNumber)}`;
    case 'INTERNATIONAL':
      return `${countryNameOf(account.countryCode, countries)} · ${account.bankName} · ${maskAccount(account.iban ?? account.accountNumber ?? '')}`;
  }
};

export const routingLabelFor = (countryCode: string): string => {
  const meta = ROUTING_CODES[countryCode] ?? DEFAULT_ROUTING;
  return meta.label;
};

/** Rows for the review screen (§5.4). */
export const reviewRows = (account: NewAccountInput, countries: CountryRef[]): LabelValue[] => {
  switch (account.kind) {
    case 'MOBILE_TRANSFER': {
      const rows: LabelValue[] = [{ label: 'Type', value: SUB_TYPE_LABELS[account.subType] }];
      if (account.subType === 'UPI_ID') {
        rows.push({ label: 'UPI ID', value: account.upiId ?? '' });
      } else if (account.subType === 'MOBILE_NUMBER') {
        rows.push({ label: 'Mobile', value: formatMobile(account.mobile ?? '') });
      } else if (account.subType === 'MERCHANT_UPI') {
        rows.push({ label: 'Merchant UPI ID', value: account.upiId ?? '' });
        if (account.merchantName) rows.push({ label: 'Merchant name', value: account.merchantName });
      } else if (account.subType === 'MOBILE_RECHARGE') {
        rows.push(
          { label: 'Mobile', value: formatMobile(account.mobile ?? '') },
          { label: 'Operator', value: account.operator ?? '' },
          { label: 'Circle', value: account.circle ?? '' }
        );
      } else {
        rows.push(
          { label: 'Category', value: account.billCategory ?? '' },
          { label: 'Biller', value: account.biller ?? '' },
          { label: 'Consumer number', value: account.consumerNumber ?? '' }
        );
      }
      rows.push({ label: 'Holder name', value: account.holderName });
      return rows;
    }
    case 'LOCAL_TRANSFER': {
      const rows: LabelValue[] = [
        { label: 'Type', value: 'Local Transfer' },
        { label: 'Bank', value: account.bankName },
      ];
      if (account.branchName) {
        rows.push({
          label: 'Branch',
          value: [account.branchName, account.branchCity].filter(Boolean).join(', '),
        });
      }
      rows.push(
        { label: 'IFSC', value: account.ifsc },
        { label: 'Account no.', value: maskAccount(account.accountNumber) },
        { label: 'Account name', value: account.accountName }
      );
      if (isNovaIfsc(account.ifsc)) rows.push({ label: 'Transfer', value: 'NovaBank · instant' });
      return rows;
    }
    case 'INTERNATIONAL': {
      const rows: LabelValue[] = [
        { label: 'Type', value: 'International Transfer' },
        { label: 'Country', value: countryNameOf(account.countryCode, countries) },
        { label: 'Bank', value: account.bankName },
        { label: 'Account name', value: account.accountName },
      ];
      if (account.iban) rows.push({ label: 'IBAN', value: maskAccount(account.iban) });
      else rows.push({ label: 'Account no.', value: maskAccount(account.accountNumber ?? '') });
      if (account.routingCode) {
        rows.push({ label: routingLabelFor(account.countryCode), value: account.routingCode });
      }
      if (account.swift) rows.push({ label: 'SWIFT/BIC', value: account.swift });
      rows.push({ label: 'Currency', value: account.currency });
      const bankAddr = formatAddress(account.bankAddress);
      if (bankAddr) rows.push({ label: 'Bank address', value: bankAddr });
      rows.push({ label: 'Account address', value: formatAddress(account.accountAddress) });
      return rows;
    }
  }
};
